"use client";

import { useEffect, useRef, useState } from "react";
import Icon from "@/components/ui/AppIcon";
import { useLanguage } from "@/context/LanguageContext";

export default function HowToBookSection() {
  const [activeStep, setActiveStep] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const { t } = useLanguage();

  const steps = [
    {
      id: "step_select",
      number: 1,
      titleKey: "htb_s1_title" as const,
      descKey: "htb_s1_desc" as const,
      icon: "CursorArrowRaysIcon" as const,
    },
    {
      id: "step_date",
      number: 2,
      titleKey: "htb_s2_title" as const,
      descKey: "htb_s2_desc" as const,
      icon: "CalendarIcon" as const,
    },
    {
      id: "step_confirm",
      number: 3,
      titleKey: "htb_s3_title" as const,
      descKey: "htb_s3_desc" as const,
      icon: "CheckCircleIcon" as const,
    },
    {
      id: "step_enjoy",
      number: 4,
      titleKey: "htb_s4_title" as const,
      descKey: "htb_s4_desc" as const,
      icon: "SparklesIcon" as const,
    },
  ];

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "-40% 0% -40% 0%",
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const stepNum = parseInt(entry.target.getAttribute("data-step") || "0");
          setActiveStep(stepNum);
        }
      });
    }, observerOptions);

    const stepElements = sectionRef.current?.querySelectorAll("[data-step]");
    stepElements?.forEach((el) => observer.observe(el));

    return () => {
      stepElements?.forEach((el) => observer.unobserve(el));
    };
  }, []);

  return (
    <section ref={sectionRef} className="py-20 md:py-32 bg-muted/30">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16 reveal will-animate">
          <span className="text-accent font-semibold text-sm uppercase tracking-wider">
            {t("htb_badge")}
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-foreground mt-4 mb-6">
            {t("htb_title")}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {t("htb_desc")}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Steps (Left Column) */}
          <div className="space-y-12 lg:sticky lg:top-32">
            {steps.map((step, index) => (
              <div
                key={step.id}
                data-step={index}
                className={`transition-all duration-500 ${activeStep === index ? "opacity-100" : "opacity-40"}`}
              >
                <div className="flex items-start gap-6">
                  <div className={`flex-shrink-0 w-16 h-16 rounded-2xl flex items-center justify-center transition-colors ${activeStep === index ? "bg-accent" : "bg-muted"}`}>
                    <Icon
                      name={step.icon}
                      size={32}
                      className={activeStep === index ? "text-white" : "text-muted-foreground"}
                      variant={activeStep === index ? "solid" : "outline"}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm text-muted-foreground mb-2">{t("htb_step")} {step.number}</div>
                    <h3 className={`text-2xl font-bold mb-3 transition-colors ${activeStep === index ? "text-foreground" : "text-muted-foreground"}`}>
                      {t(step.titleKey)}
                    </h3>
                    <p className={`leading-relaxed transition-colors ${activeStep === index ? "text-foreground" : "text-muted-foreground"}`}>
                      {t(step.descKey)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Visual (Right Column - Sticky) */}
          <div className="lg:sticky lg:top-32">
            <div className="bg-card rounded-3xl p-8 shadow-xl">
              <div className="aspect-square bg-gradient-to-br from-primary/20 via-accent/20 to-secondary/20 rounded-2xl flex items-center justify-center relative overflow-hidden">
                {[
                  { icon: "CursorArrowRaysIcon" as const, color: "text-accent", border: "border-accent" },
                  { icon: "CalendarIcon" as const, color: "text-primary", border: "border-primary" },
                  { icon: "CheckCircleIcon" as const, color: "text-success", border: "border-success" },
                  { icon: "SparklesIcon" as const, color: "text-secondary", border: "border-secondary" },
                ].map((item, i) => (
                  <div key={i} className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 ${activeStep === i ? "opacity-100" : "opacity-0"}`}>
                    <div className={`w-48 h-48 rounded-full border-4 ${item.border} flex items-center justify-center`}>
                      <Icon name={item.icon} size={80} className={item.color} variant={i === 2 ? "solid" : "outline"} />
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 text-center">
                <p className="text-muted-foreground mb-4">{t("htb_ready")}</p>
                <a
                  href="https://wa.me/21654604948"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-8 py-3 bg-success text-white rounded-full font-semibold hover:bg-success/90 transition-all hover:scale-105"
                >
                  <Icon name="ChatBubbleLeftRightIcon" size={20} variant="solid" />
                  <span>{t("htb_whatsapp")}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}